import React, { useState } from 'react';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const PrivacySettings = ({ onChange }) => {
  const [privacySettings, setPrivacySettings] = useState({
    shareMoodData: true,
    shareAvailability: true,
    sharePreferences: true,
    enableAIMediation: true,
    allowCompatibilityAnalysis: true,
    receiveAISuggestions: true,
    moodTrackingVisible: true,
    encounterHistoryAccess: true,
    showOnlineStatus: true,
    allowDataExport: true
  });

  const handleToggle = (name) => {
    setPrivacySettings(prev => ({ ...prev, [name]: !prev?.[name] }));
    onChange?.();
  };

  const privacyGroups = [
    {
      title: 'Data Sharing with Partner',
      description: 'Control what information is shared with your partner',
      icon: 'Users',
      settings: [
        {
          key: 'shareMoodData',
          label: 'Share Mood Data',
          description: 'Allow your partner to see your mood tracking entries and emotional states'
        },
        {
          key: 'shareAvailability',
          label: 'Share Availability',
          description: 'Let your partner view your calendar availability for scheduling'
        },
        {
          key: 'sharePreferences',
          label: 'Share Preferences',
          description: 'Share your intimacy preferences and desires with your partner'
        }
      ]
    },
    {
      title: 'AI Communication Settings',
      description: 'Manage how AI assists in your relationship communication',
      icon: 'Bot',
      settings: [
        {
          key: 'enableAIMediation',
          label: 'Enable AI Mediation',
          description: 'Allow AI to help mediate and translate sensitive communications between partners'
        },
        {
          key: 'allowCompatibilityAnalysis',
          label: 'Compatibility Analysis',
          description: 'Let AI analyze patterns to provide compatibility insights and timing suggestions'
        },
        {
          key: 'receiveAISuggestions',
          label: 'Receive AI Suggestions',
          description: 'Get proactive suggestions from AI based on your relationship patterns'
        }
      ]
    },
    {
      title: 'Visibility & Access',
      description: 'Control visibility of your activities and history',
      icon: 'Eye',
      settings: [
        {
          key: 'moodTrackingVisible',
          label: 'Mood Tracking Visibility',
          description: 'Make your mood entries visible to your partner in real-time'
        },
        {
          key: 'encounterHistoryAccess',
          label: 'Encounter History Access',
          description: 'Allow your partner to view shared encounter history and feedback'
        },
        {
          key: 'showOnlineStatus',
          label: 'Show Online Status',
          description: 'Display when you are actively using the app'
        }
      ]
    },
    {
      title: 'Data Management',
      description: 'Control your personal data and export options',
      icon: 'Database',
      settings: [
        {
          key: 'allowDataExport',
          label: 'Allow Data Export',
          description: 'Enable the ability to export your personal data at any time'
        }
      ]
    }
  ];

  return (
    <div className="space-y-6">
      <div className="bg-accent/30 border border-accent rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Icon name="Shield" size={20} color="var(--color-primary)" />
          <div>
            <h4 className="text-sm font-semibold text-foreground mb-1">
              Privacy Protection
            </h4>
            <p className="text-sm text-muted-foreground">
              Your privacy is paramount. All settings maintain strict separation between individual and shared data. Changes sync across your partner's account where applicable.
            </p>
          </div>
        </div>
      </div>

      {privacyGroups?.map((group, index) => (
        <div key={index} className="border-t border-border pt-6 first:border-t-0 first:pt-0">
          <div className="flex items-start space-x-3 mb-4">
            <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon name={group?.icon} size={20} color="var(--color-primary)" />
            </div>
            <div>
              <h3 className="text-lg font-heading font-semibold text-foreground">
                {group?.title}
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                {group?.description}
              </p>
            </div>
          </div>

          <div className="space-y-4 ml-0 md:ml-13">
            {group?.settings?.map((setting) => (
              <Checkbox
                key={setting?.key}
                checked={privacySettings?.[setting?.key]}
                onChange={() => handleToggle(setting?.key)}
                label={setting?.label}
                description={setting?.description}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default PrivacySettings;